import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { AppButton, AppSpin, AppTable, AppTableActionButton, AppTooltip } from '@/components/atoms';
import { CollapsibleCard } from '@/components/molecules';
import useAppModal from '@/hooks/useAppModal';
import { useCheckinMailList } from '@/hooks/main/useCheckinMailList';
import { useCheckinMailDelete } from '@/hooks/main/useCheckinMailEdit';
import type { CheckinMailItem } from '@/services/main/checkInMain/checkinMailService';
import PatientPageHeader from '../components/PatientPageHeader';
import { NotFound } from '@/pages/main/system/NotFound';
import { TENANT_ROUTES } from '@/constants/routes';
import { DELETE_CONFIRM_DATA_MESSAGE } from '@/constants/messages';

const PatientCheckinMailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { confirm } = useAppModal();
  const params = useParams<{ tenant?: string; id?: string }>();

  const tenant = params.tenant;
  const patientId = params.id ?? '';

  const { data, isLoading, isFetching, isError } = useCheckinMailList(patientId);
  const deleteMutation = useCheckinMailDelete(patientId);

  const patient = data?.patient;
  const list = data?.list ?? [];

  const patientName = patient?.current_patient_information?.name ?? '—';

  const handleNew = () => {
    navigate(TENANT_ROUTES.CHECKIN_MAILS.CREATE(tenant ?? '', patientId));
  };

  const handleEditClick = (record: CheckinMailItem) => {
    navigate(TENANT_ROUTES.CHECKIN_MAILS.EDIT(tenant ?? '', patientId, record.id));
  };

  const handleDeleteClick = (record: CheckinMailItem) => {
    confirm({
      title: DELETE_CONFIRM_DATA_MESSAGE,
      okText: 'OK',
      cancelText: 'キャンセル',
      okButtonProps: { danger: true },
      onOk: () => deleteMutation.mutateAsync(record.id),
    });
  };

  const columns = useMemo(
    () => [
      {
        title: '連番',
        key: 'no',
        width: 50,
        align: 'center' as const,
        render: (_: unknown, __: CheckinMailItem, index: number) => index + 1,
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
        align: 'left' as const,
        className: 'min-w-0',
        ellipsis: false,
        sorter: (a: CheckinMailItem, b: CheckinMailItem) =>
          (a.email ?? '').localeCompare(b.email ?? ''),
        render: (_: unknown, record: CheckinMailItem) => {
          const text = record.email?.trim() || '—';
          return (
            <AppTooltip title={text} showOnlyOnOverflow>
              <div className="break-all line-clamp-2">{text}</div>
            </AppTooltip>
          );
        },
      },
      {
        title: '状態',
        dataIndex: 'is_status',
        key: 'is_status',
        width: '30%',
        align: 'center' as const,
        sorter: (a: CheckinMailItem, b: CheckinMailItem) =>
          (a.is_status ? 1 : 0) - (b.is_status ? 1 : 0),
        render: (v: boolean) => (v ? '有効' : '無効'),
      },
      {
        title: '操作',
        key: 'actions',
        width: 170,
        align: 'center' as const,
        render: (_: unknown, record: CheckinMailItem) => (
          <div className="flex justify-center gap-1">
            <AppTableActionButton
              action="edit"
              onClick={e => {
                e.stopPropagation();
                handleEditClick(record);
              }}
              title="編集"
            >
              編集
            </AppTableActionButton>
            <AppTableActionButton
              action="delete"
              onClick={e => {
                e.stopPropagation();
                handleDeleteClick(record);
              }}
              title="削除"
            >
              削除
            </AppTableActionButton>
          </div>
        ),
      },
    ],
    [confirm, deleteMutation.isPending]
  );

  const dataSource = useMemo(() => list.map((x: CheckinMailItem) => ({ ...x, key: x.id })), [list]);

  const showLoading = isLoading || isFetching;

  if (isError && !data) {
    return <NotFound />;
  }

  return (
    <AppSpin spinning={showLoading}>
      <div className="pb-24">
        <CollapsibleCard title="チェックイン通知一覧">
          <PatientPageHeader
            label={`${patient?.serial_number ?? patientId}：${patientName}`}
            patientId={patientId}
            tenant={tenant}
            navigate={path => navigate(path)}
            activeKey="checkin-mails"
            actions={
              <AppButton
                type="primary"
                onClick={handleNew}
                disabled={!patientId}
                className="flex items-center justify-center gap-2 px-4 py-2 font-medium md:w-auto"
              >
                <i className="fa fa-file-text-o" />
                新規作成
              </AppButton>
            }
          />

          <div className="app-table-last-col-fit">
            <AppTable<CheckinMailItem>
              columns={columns}
              dataSource={dataSource}
              rowKey="id"
              pagination={false}
              tableLayout="auto"
              loading={false}
              recordCount={list.length}
            />
          </div>
        </CollapsibleCard>
      </div>
    </AppSpin>
  );
};

export default PatientCheckinMailsPage;
